// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/ArtworkPreviewThumb.test.tsx
import { Image as ImageIcon } from "@phosphor-icons/react";
import { getDocument } from "pdfjs-dist";
import { useEffect, useState } from "react";

import { getSignedPdfUrlResult } from "../../services/files";
import { ensurePdfjsWorker } from "../../utils/pdfjsWorker";

/** Longest side of the raster, in CSS pixels. The slot is 48px; 2x stays sharp. */
const PREVIEW_LONG_SIDE = 96;
const NOT_READY_POLL_MS = 3_000;
const NOT_READY_DEADLINE_MS = 60_000;

/** How many page-1 rasters stay cached. Older ones drop so a long project cannot keep every bitmap. */
export const ARTWORK_PREVIEW_CACHE_LIMIT = 24;

const previewJobs = new Map<string, Promise<string | null>>();

export function clearArtworkPreviewCache(): void {
  previewJobs.clear();
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// why: warhol stores the reviewed artwork as a PDF and leaves thumbnailUrl null.
// The list card cannot use that PDF as an <img>. Page 1 is rasterized once the
// file can be fetched, and the placeholder stays up until that bitmap exists.
async function rasterizeArtworkPreview(
  sourceUrl: string,
): Promise<string | null> {
  const deadline = Date.now() + NOT_READY_DEADLINE_MS;
  let signed = await getSignedPdfUrlResult(sourceUrl);
  while (signed.status === "not_ready" && Date.now() < deadline) {
    await delay(NOT_READY_POLL_MS);
    signed = await getSignedPdfUrlResult(sourceUrl);
  }
  if (signed.status !== "ok") return null;

  ensurePdfjsWorker();
  const pdf = await getDocument(signed.url).promise;
  try {
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const longSide = Math.max(base.width, base.height);
    const scale = longSide > 0 ? PREVIEW_LONG_SIDE / longSide : 1;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const context = canvas.getContext("2d");
    if (!context) return null;
    await page.render({ canvasContext: context, viewport }).promise;
    return canvas.toDataURL("image/jpeg", 0.82);
  } finally {
    await pdf.destroy();
  }
}

function previewFor(sourceUrl: string): Promise<string | null> {
  const existing = previewJobs.get(sourceUrl);
  if (existing) return existing;
  const job = rasterizeArtworkPreview(sourceUrl).catch(() => null);
  previewJobs.set(sourceUrl, job);
  while (previewJobs.size > ARTWORK_PREVIEW_CACHE_LIMIT) {
    // why: the url just stored is the newest key, so the oldest entry is a different url and the map is not empty
    const oldest = previewJobs.keys().next().value as string;
    previewJobs.delete(oldest);
  }
  void job.then((url) => {
    if (!url) previewJobs.delete(sourceUrl);
  });
  return job;
}

export function ArtworkPreviewThumb({
  sourceUrl,
  testId,
}: {
  sourceUrl: string;
  testId: string;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void previewFor(sourceUrl).then((url) => {
      if (!cancelled && url) setDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [sourceUrl]);

  if (!dataUrl) {
    return <ImageIcon size={18} weight="fill" aria-hidden="true" />;
  }

  return (
    <img
      src={dataUrl}
      alt=""
      aria-hidden="true"
      data-testid={testId}
      className="size-full object-cover"
    />
  );
}
